const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface ScopeInfo {
  id: string;
  label: string;
  description: string;
  group: string;
}

export interface ApiKey {
  id: number;
  name: string;
  description: string | null;
  contact_email: string | null;
  key_type: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiKeyCreated extends ApiKey {
  raw_key: string;
}

export interface ApiKeyAccessLog {
  id: number;
  api_key_id: number | null;
  api_key_name: string | null;
  method: string;
  path: string;
  status_code: number;
  ip_address: string | null;
  user_agent: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface ApiKeyFormData {
  name: string;
  description: string;
  contact_email: string;
  scopes: string[];
  expires_at: string;
  is_active?: boolean;
}

/** Scopes commonly needed for Cross-Validation integration */
export const CV_INTEGRATION_SCOPES = [
  "read:instances",
  "read:quotes",
  "read:signals",
];

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || response.statusText || "Request failed");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const fetchScopes = () => request<ScopeInfo[]>("/api/api-keys/scopes");

export const fetchApiKeys = () => request<ApiKey[]>("/api/api-keys");

export const createApiKey = (data: ApiKeyFormData) =>
  request<ApiKeyCreated>("/api/api-keys", {
    method: "POST",
    body: JSON.stringify({
      name: data.name,
      description: data.description || null,
      contact_email: data.contact_email || null,
      scopes: data.scopes,
      expires_at: data.expires_at || null,
    }),
  });

export const updateApiKey = (id: number, data: Partial<ApiKeyFormData>) =>
  request<ApiKey>(`/api/api-keys/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      ...(data.name !== undefined ? { name: data.name } : {}),
      ...(data.description !== undefined ? { description: data.description || null } : {}),
      ...(data.contact_email !== undefined ? { contact_email: data.contact_email || null } : {}),
      ...(data.scopes !== undefined ? { scopes: data.scopes } : {}),
      ...(data.expires_at !== undefined ? { expires_at: data.expires_at || null } : {}),
      ...(data.is_active !== undefined ? { is_active: data.is_active } : {}),
    }),
  });

export const rotateApiKey = (id: number) =>
  request<ApiKeyCreated>(`/api/api-keys/${id}/rotate`, { method: "POST" });

export const deleteApiKey = (id: number) =>
  request<void>(`/api/api-keys/${id}`, { method: "DELETE" });

export const fetchApiKeyActivity = (id: number, limit = 100) =>
  request<ApiKeyAccessLog[]>(`/api/api-keys/${id}/activity?limit=${limit}`);

export const fetchAllActivity = (limit = 100) =>
  request<ApiKeyAccessLog[]>(`/api/api-keys/activity?limit=${limit}`);

export function groupScopes(scopes: ScopeInfo[]): Record<string, ScopeInfo[]> {
  return scopes.reduce<Record<string, ScopeInfo[]>>((acc, scope) => {
    const group = scope.group || "General";
    if (!acc[group]) acc[group] = [];
    acc[group].push(scope);
    return acc;
  }, {});
}
